'use client';
import { useCallback, useEffect, useRef, useState, useTransition } from 'react';
import { ChatClient } from './chat-client';
const DEBUG = false;
/**
 * Debug logging helper
 *
 * @param {string} message - The message to log
 * @param {any} [data] - The data to log
 */
const debugLog = (message, data) => {
    if (DEBUG) {
        console.log(`[useStreamChat] ${message}`, data || '');
    }
};
/**
 * Custom hook for managing chat messages and state
 *
 * @param {UseStreamChatConfig} config - The configuration for the chat client
 * @returns {UseStreamChatResult} The result of the chat client
 */
export const useStreamChat = (config) => {
    debugLog('Initializing useStreamChat with config', config);
    // Use transitions for state updates to prevent UI blocking
    const [, startTransition] = useTransition();
    // State management
    const [chatId, setChatId] = useState(null);
    const [messages, setMessages] = useState([]);
    const [isTyping, setIsTyping] = useState(false);
    const [error, setError] = useState(null);
    // Refs for stable values across renders
    const clientRef = useRef(null);
    const configRef = useRef(config);
    const mountedRef = useRef(true);
    // Create client with stable event handlers
    const getClient = useCallback(() => {
        if (clientRef.current && mountedRef.current) {
            return clientRef.current;
        }
        if (!mountedRef.current) {
            debugLog('Component is not mounted, skipping client creation');
            return null;
        }
        debugLog('Creating new ChatClient');
        const client = new ChatClient({
            baseUrl: configRef.current.baseUrl,
            tenantId: configRef.current.tenantId,
            agentId: configRef.current.agentId,
            /**
             * On message received
             *
             * @param {ChatMessage} message - The message received
             */
            onMessage: (message) => {
                if (!mountedRef.current)
                    return;
                setMessages((prev) => {
                    // Handle temp message replacement if needed
                    if (message.tempIdToReplace) {
                        const index = prev.findIndex((m) => m.id === message.tempIdToReplace);
                        // Create completely new message and array references
                        const updatedMessage = Object.assign({}, message);
                        delete updatedMessage.tempIdToReplace;
                        if (index >= 0) {
                            return [
                                ...prev.slice(0, index),
                                updatedMessage,
                                ...prev.slice(index + 1),
                            ];
                        }
                    }
                    // Handle existing message update
                    const index = prev.findIndex((m) => m.id === message.id);
                    if (index >= 0) {
                        return [
                            ...prev.slice(0, index),
                            Object.assign({}, message),
                            ...prev.slice(index + 1),
                        ];
                    }
                    // New message
                    return [...prev, Object.assign({}, message)];
                });
            },
            /**
             * On error
             *
             * @param {Error} error - The error that occurred
             */
            onError: (error) => {
                if (!mountedRef.current)
                    return;
                debugLog('Error occurred', error);
                setError(error);
                setMessages((prev) => {
                    return [
                        ...prev,
                        {
                            id: `error_${Date.now()}`,
                            role: 'error',
                            content: error.message,
                        },
                    ];
                });
            },
            /**
             * On typing state change
             *
             * @param {boolean} isTyping - The typing state
             */
            onTypingStateChange: (isTyping) => {
                if (!mountedRef.current)
                    return;
                setIsTyping(isTyping);
            },
            /**
             * On chat started
             *
             * @param {string} chatId - The chat ID
             */
            onChatStarted: (chatId) => {
                if (!mountedRef.current)
                    return;
                setChatId(chatId);
            },
        });
        if (mountedRef.current) {
            clientRef.current = client;
            return client;
        }
        return null;
    }, []);
    // Update config ref when config changes
    useEffect(() => {
        if (!mountedRef.current)
            return;
        const hasConfigChanged = config.baseUrl !== configRef.current.baseUrl ||
            config.tenantId !== configRef.current.tenantId ||
            config.agentId !== configRef.current.agentId ||
            config.userId !== configRef.current.userId;
        if (hasConfigChanged) {
            debugLog('Config changed, updating client');
            configRef.current = config;
            // Only clear client if mounted
            if (mountedRef.current) {
                clientRef.current = null;
            }
        }
    }, [config]);
    // Send message function
    const sendMessage = useCallback(async (message) => {
        if (!mountedRef.current) {
            debugLog('Component is not mounted, skipping message send');
            return;
        }
        const client = getClient();
        if (!client) {
            debugLog('Failed to get client');
            return;
        }
        try {
            const processedMessage = await client.sendMessage(message);
            if (mountedRef.current) {
                setError(null);
                startTransition(() => {
                    setMessages((prev) => [
                        ...prev,
                        {
                            id: `user_${Date.now()}`,
                            role: 'user',
                            content: processedMessage,
                        },
                    ]);
                });
            }
        }
        catch (error) {
            debugLog('Failed to send message', error);
            if (mountedRef.current) {
                setError(error);
            }
        }
    }, [getClient]);
    // Start new chat function
    const startNewChat = useCallback(() => {
        if (!mountedRef.current) {
            debugLog('Component is not mounted, skipping new chat');
            return;
        }
        setMessages([]);
        setError(null);
        const client = getClient();
        if (client) {
            client.startNewChat();
        }
    }, [getClient]);
    // Cleanup on unmount
    useEffect(() => {
        mountedRef.current = true;
        return () => {
            debugLog('Component unmounting, cleaning up');
            mountedRef.current = false;
            // Clear state
            setMessages([]);
            setError(null);
            setIsTyping(false);
            // Clean up client
            if (clientRef.current) {
                clientRef.current.startNewChat();
                clientRef.current = null;
            }
        };
    }, []);
    return {
        chatId,
        messages,
        isTyping,
        error,
        sendMessage,
        startNewChat,
    };
};
//# sourceMappingURL=useStreamChat.js.map